import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Users, MessageSquare, Wand2, Send } from "lucide-react";

export default function Hiring() {
  const [devs, setDevs] = useState([]);
  const [profile, setProfile] = useState(null);
  const [pid, setPid] = useState("");
  const [brief, setBrief] = useState("");
  const [session, setSession] = useState(null);
  const [turn, setTurn] = useState("");
  const [contractId, setContractId] = useState("");
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");

  useEffect(() => { api.get("/directory/").then(r => setDevs(r.data.developers || [])).catch(() => {}); }, []);

  const startScoping = async () => {
    if (!pid || !brief.trim()) { toast.error("Enter a project ID and a brief"); return; }
    try { const { data } = await api.post("/hiring/scoping/start", { project_id: pid, brief }); setSession(data); toast.success("Scoping session started"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sendTurn = async () => {
    if (!session || !turn.trim()) return;
    try { const { data } = await api.post("/hiring/scoping/turn", { session_id: session.id, message: turn });
      setSession(s => ({ ...s, turns_remaining: data.turns_remaining })); setTurn(""); toast.success(`Turn logged (${data.turns_remaining} left)`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const viewProfile = async (uid) => {
    try { const { data } = await api.get(`/directory/profile/${uid}`); setProfile(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const loadMessages = async () => {
    if (!contractId.trim()) return;
    try { const { data } = await api.get(`/hiring/messages/${contractId}`); setMessages(data || []); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sendMessage = async () => {
    if (!contractId.trim() || !msg.trim()) return;
    try { await api.post("/hiring/messages", { contract_id: contractId, body: msg }); setMsg(""); loadMessages(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Shell active="hiring">
      <h1 className="k-heading" style={{ fontSize: 30, marginTop: 0 }}>Hire a developer</h1>
      <p className="k-muted" style={{ marginTop: 6 }}></p>

      {/* #113 scoping-brief wizard */}
      <div className="k-container" style={{ padding: 18, marginBottom: 16 }} data-testid="scoping-wizard">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Wand2 size={16} color="var(--accent-primary)" /><h3 className="k-heading" style={{ fontSize: 16, margin: 0 }}>Scoping brief wizard</h3>
        </div>
        {!session ? (
          <>
            <input className="k-control" data-testid="scoping-project" placeholder="Project ID" value={pid} onChange={e => setPid(e.target.value)} style={{ marginBottom: 8 }} />
            <textarea className="k-control" data-testid="scoping-brief" rows={3} placeholder="Describe what you need built, in plain English…" value={brief} onChange={e => setBrief(e.target.value)} style={{ marginBottom: 8 }} />
            <button className="k-btn k-btn-primary" data-testid="scoping-start" onClick={startScoping}>Start scoping</button>
          </>
        ) : (
          <div data-testid="scoping-session">
            <p className="k-muted" style={{ fontSize: 13 }}>Session {session.id.slice(0, 8)} · {session.turns_remaining} turns left · {session.sessions_remaining} free sessions left</p>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="k-control" data-testid="scoping-turn" placeholder="Answer / refine…" value={turn} onChange={e => setTurn(e.target.value)} onKeyDown={e => e.key === "Enter" && sendTurn()} />
              <button className="k-btn k-btn-primary" data-testid="scoping-send" onClick={sendTurn}><Send size={14} /></button>
            </div>
          </div>
        )}
      </div>

      {/* #115 developer directory + profile */}
      <div className="k-container" style={{ padding: 18, marginBottom: 16 }} data-testid="developer-directory">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Users size={16} color="var(--accent-primary)" /><h3 className="k-heading" style={{ fontSize: 16, margin: 0 }}>Developer directory</h3>
        </div>
        {devs.length === 0 && <p className="k-muted" data-testid="directory-empty" style={{ fontSize: 13 }}>No developer profiles yet.</p>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 10 }}>
          {devs.map(d => (
            <div key={d.user_id} className="k-control" data-testid={`dev-${d.user_id}`} style={{ cursor: "pointer" }} onClick={() => viewProfile(d.user_id)}>
              <div className="k-heading" style={{ fontSize: 14, fontWeight: 600 }}>{d.display_name} <span className="k-badge">{d.badge_status}</span></div>
              <div className="k-muted" style={{ fontSize: 12, marginTop: 4 }}>Score {d.reputation_status?.score ?? 0} · {(d.jurisdiction_specializations || []).join(", ") || "—"}</div>
            </div>
          ))}
        </div>
        {profile && (
          <div className="k-control" data-testid="dev-profile" style={{ marginTop: 12 }}>
            <div className="k-heading" style={{ fontWeight: 700 }}>{profile.display_name} <span className="k-badge k-badge-accent">{profile.badge_status}</span></div>
            <p className="k-muted" style={{ fontSize: 12 }}>Completed {profile.reputation_status?.completed_contracts} · on-time {profile.reputation_status?.on_time_deliveries} · rating {profile.reputation_status?.average_rating ?? "—"}</p>
            <p className="k-muted" style={{ fontSize: 12 }}>APIs: {(profile.api_mastery_tags || []).join(", ") || "—"}</p>
            <p className="k-muted" style={{ fontSize: 12 }}>Regions: {(profile.jurisdiction_specializations || []).join(", ") || "—"}</p>
          </div>
        )}
      </div>

      {/* #116 in-app messaging */}
      <div className="k-container" style={{ padding: 18 }} data-testid="hiring-messaging">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <MessageSquare size={16} color="var(--accent-primary)" /><h3 className="k-heading" style={{ fontSize: 16, margin: 0 }}>Messages</h3>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input className="k-control" data-testid="msg-contract" placeholder="Contract ID" value={contractId} onChange={e => setContractId(e.target.value)} />
          <button className="k-btn k-btn-ghost" data-testid="msg-load" onClick={loadMessages}>Load</button>
        </div>
        <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 8 }}>
          {messages.map(m => (
            <div key={m.id} className="k-control" style={{ marginBottom: 6 }} data-testid="hire-message">
              <div className="k-muted mono" style={{ fontSize: 10 }}>{m.from_user.slice(0, 8)} · {String(m.at).slice(0, 19).replace("T", " ")}</div>
              <div style={{ fontSize: 13 }}>{m.body}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input className="k-control" data-testid="msg-body" placeholder="Type a message…" value={msg} onChange={e => setMsg(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMessage()} />
          <button className="k-btn k-btn-primary" data-testid="msg-send" onClick={sendMessage}><Send size={14} /> Send</button>
        </div>
      </div>
    </Shell>
  );
}
